import { useWidgetData, aggregate, currencyFmt } from './shared';
import type {
  TableSummaryColumn,
  TableSummaryDashboardWidget,
  TableSummaryConfig,
  WidgetAggregation,
} from '../../types/panelConfig';

interface TableSummaryWidgetProps {
  widget: TableSummaryDashboardWidget;
  sourceId: number | null | undefined;
  parameters?: Record<string, string>;
}

type Row = Record<string, unknown>;

function collapse(rows: Row[], cfg: TableSummaryConfig): Row[] {
  if (!cfg.groupBy) return rows.slice();
  const groupBy = cfg.groupBy;
  const buckets = new Map<string, Row[]>();
  for (const row of rows) {
    const key = String(row[groupBy] ?? '');
    if (!key) continue;
    const existing = buckets.get(key);
    if (existing) existing.push(row);
    else buckets.set(key, [row]);
  }
  return Array.from(buckets.entries()).map(([key, group]) => {
    const out: Row = { [groupBy]: key };
    for (const col of cfg.columns) {
      if (col.field === groupBy) continue;
      const method: WidgetAggregation = col.agg ?? 'sum';
      out[col.field] = aggregate(group, col.field, method);
    }
    return out;
  });
}

function sagaYasli(type?: string): boolean {
  return !!type && type !== 'bar';
}

const yuzdeFmt = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const ondalikFmt = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function formatCell(value: unknown, type?: string): string {
  if (value === null || value === undefined) return '—';
  if (type === 'currency') return currencyFmt.format(Number(value) || 0);
  if (type === 'number') return new Intl.NumberFormat('tr-TR').format(Number(value) || 0);
  if (type === 'percent') return `%${yuzdeFmt.format(Number(value) || 0)}`;
  if (type === 'decimal') return ondalikFmt.format(Number(value) || 0);
  return String(value);
}

type EsikTonu = 'kirmizi' | 'sari' | 'yesil';

const ESIK_RENK: Record<EsikTonu, { background: string; color: string }> = {
  kirmizi: { background: 'var(--color-error-bg)', color: 'var(--color-error)' },
  sari: { background: 'color-mix(in srgb, var(--color-warning) 16%, transparent)', color: 'var(--color-warning)' },
  yesil: { background: 'color-mix(in srgb, var(--color-success) 14%, transparent)', color: 'var(--color-success)' },
};

// Ton, EKRANDA görünen (yuvarlanmış) sayıdan seçilir: -0,04 "%0,0" yazılıp kırmızı görünmesin.
function esikTonu(value: unknown, col: TableSummaryColumn): EsikTonu | null {
  if (!col.esik || value === null || value === undefined || value === '') return null;
  const hane = col.type === 'percent' ? 1 : col.type === 'decimal' ? 2 : 0;
  const n = Math.round((Number(value) || 0) * 10 ** hane) / 10 ** hane;
  if (col.esik.kirmizi !== undefined && n < col.esik.kirmizi) return 'kirmizi';
  if (col.esik.sari !== undefined && n < col.esik.sari) return 'sari';
  return 'yesil';
}

export function TableSummaryWidget({ widget, sourceId, parameters }: TableSummaryWidgetProps) {
  const { data, isLoading } = useWidgetData(sourceId, `table-${widget.reportCode}`, parameters);
  const cfg = widget.config;
  if (!cfg) {
    return <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">Yapılandırma hatası</div>;
  }

  let rows = collapse(data ?? [], cfg);
  if (cfg.sortBy) {
    const dir = cfg.sortDir === 'asc' ? 1 : -1;
    const sortBy = cfg.sortBy;
    rows = rows.slice().sort((a, b) => (Number(a[sortBy]) - Number(b[sortBy])) * dir);
  }
  // 'bar' kolonları: satırın TOPLAMDAKİ payı (topN'den önce, bütün satırlar). En büyüğe göre
  // ölçeklemek "Pay" başlığı altında birinciyi %100 gösteriyordu (S309, kullanıcı: "saçma").
  const barToplam: Record<string, number> = {};
  for (const c of cfg.columns) {
    if (c.type === 'bar') barToplam[c.field] = rows.reduce((t, r) => t + Math.max(0, Number(r[c.field]) || 0), 0);
  }
  if (cfg.topN) rows = rows.slice(0, cfg.topN);
  const pay = (row: Row, field: string): number =>
    barToplam[field] ? Math.max(0, Number(row[field]) || 0) / barToplam[field] : 0;
  const sabitYerlesim = cfg.columns.some((c) => c.width);
  const renk = (row: Row): string | undefined =>
    cfg.colorBy ? cfg.colorBy.map[String(row[cfg.colorBy.field] ?? '')] : undefined;

  if (isLoading) {
    return (
      <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">
        Yükleniyor…
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <div data-testid="table-summary-widget" className="text-subtle text-sm p-2">
        Veri yok
      </div>
    );
  }

  return (
    <div data-testid="table-summary-widget" className="overflow-x-auto">
      <table className="w-full text-sm" style={sabitYerlesim ? { tableLayout: 'fixed' } : undefined}>
        <thead>
          <tr className="text-subtle text-xs uppercase">
            {cfg.columns.map((c, ci) => (
              <th
                key={`${c.field}-${ci}`}
                className={`pb-2 ${c.type === 'bar' ? 'pl-6 text-left' : sagaYasli(c.type) ? 'text-right' : 'text-left'}`}
                style={c.width ? { width: c.width } : undefined}
              >
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={idx} className="border-t border-border">
              {cfg.columns.map((c, ci) =>
                c.type === 'bar' ? (
                  <td key={`${c.field}-${ci}`} className="py-2 pl-6">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span
                        data-testid={`pay-${c.field}-${idx}`}
                        className="tabular-nums text-subtle"
                        style={{ width: 36, textAlign: 'right', flexShrink: 0 }}
                      >
                        %{Math.round(pay(row, c.field) * 100)}
                      </span>
                      <div
                        data-testid={`bar-${c.field}-${idx}`}
                        style={{ flex: 1, height: 8, borderRadius: 4, background: 'var(--color-border)' }}
                      >
                        <div
                          style={{
                            height: 8,
                            borderRadius: 4,
                            width: `${pay(row, c.field) * 100}%`,
                            background: renk(row) ?? 'var(--color-primary)',
                          }}
                        />
                      </div>
                    </div>
                  </td>
                ) : (
                  <td
                    key={`${c.field}-${ci}`}
                    className={`py-2 ${sagaYasli(c.type) ? 'text-right tabular-nums' : 'text-left'}`}
                    style={c.colored && renk(row) ? { color: renk(row) } : undefined}
                  >
                    {(() => {
                      const metin = formatCell(row[c.field], c.type);
                      const ton = esikTonu(row[c.field], c);
                      if (!ton) return metin;
                      return (
                        <span
                          data-esik={ton}
                          style={{ ...ESIK_RENK[ton], padding: '2px 8px', borderRadius: 4, fontWeight: 600 }}
                        >
                          {metin}
                        </span>
                      );
                    })()}
                  </td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
